import { Tooltip } from '@material-ui/core'
import classNames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { useCalendars } from 'src/modules/calendar/hooks/use-calendars'
import { TypePolicyBadge } from 'src/modules/cancellation-policy/components/type-policy-badge'
import { Button, Icon, Loader, Modal, Typography } from 'src/modules/core/components'
import { useMessageTemplates } from 'src/modules/message-templates/hooks/use-message-templates'
import {
  useAppointmentTypes,
  useDefaultAppointmentType,
  useDeleteAppointmentType,
} from 'src/modules/user/hooks/use-appointment-types'
import { useBookingOnboarding } from 'src/modules/user/hooks/use-booking-onboarding'
import { AppointmentTypeModal } from './appointment-type-modal'
import classes from './booking-appointment-types-settings.module.scss'
import sharedClasses from './settings-booking-page-tab.module.scss'

interface BookingAppointmentTypesSettingsProps {
  userId: string
  onBack: () => void
}

// Convert minutes to readable format
function formatDuration(minutes: number): string {
  return `${minutes}m`
}

// Format minimum notice from minutes to readable format
function formatMinimumNotice(minutes: number): string {
  if (minutes === 0) return 'not set'
  if (minutes % (24 * 60) === 0) {
    const days = minutes / (24 * 60)
    return `${days} ${days === 1 ? 'day' : 'days'}`
  }
  if (minutes % 60 === 0) {
    const hours = minutes / 60
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
  }
  return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`
}

// Format buffer (returns "not set" if 0)
function formatBuffer(minutes: number): string {
  return minutes === 0 ? 'not set' : formatDuration(minutes)
}

export function BookingAppointmentTypesSettings({ userId, onBack }: BookingAppointmentTypesSettingsProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalWasClosed, setModalWasClosed] = useState(false)
  const [editingId, setEditingId] = useState<string | undefined>(undefined)
  const [deletingId, setDeletingId] = useState<string | undefined>(undefined)
  const { data: bookingOnboarding } = useBookingOnboarding()
  const { data: appointmentTypes = [], isLoading } = useAppointmentTypes(userId)
  const { data: defaultAppointmentType } = useDefaultAppointmentType(userId)
  const calendars = useCalendars()
  const messageTemplates = useMessageTemplates()
  const deleteAppointmentType = useDeleteAppointmentType()

  useEffect(() => {
    if (!isLoading && !appointmentTypes?.length && !modalWasClosed) {
      setIsModalOpen(true)
    }
  }, [isLoading, appointmentTypes?.length, modalWasClosed])

  // Create a map of calendar_id to calendar name
  const calendarNameMap = useMemo(() => {
    if (!calendars.data) return new Map<string, string>()
    const map = new Map<string, string>()
    calendars.data.forEach((calendar) => {
      map.set(calendar.id, calendar.title || calendar.calendar_id || 'Unknown Calendar')
    })
    return map
  }, [calendars.data])

  // Helper function to get calendar name
  const getCalendarName = (calendarId: string): string => {
    return calendarNameMap.get(calendarId) || 'Unknown Calendar'
  }

  // Create a map of message template_id to template name
  const messageTemplateNameMap = useMemo(() => {
    if (!messageTemplates.data) return new Map<string, string>()
    const map = new Map<string, string>()
    messageTemplates.data.forEach((template) => {
      map.set(template.id, template.template_name)
    })
    return map
  }, [messageTemplates.data])

  // Helper function to get message template name
  const getMessageTemplateName = (templateId: string | null | undefined): string => {
    if (!templateId) return 'Not set'
    return messageTemplateNameMap.get(templateId) || 'not set'
  }

  const sortedAppointmentTypes = useMemo(() => {
    const defaultAppointmentTypeId = defaultAppointmentType?.id
    return [...appointmentTypes].sort((a, b) => {
      const isADefault = a.id === defaultAppointmentTypeId
      const isBDefault = b.id === defaultAppointmentTypeId
      if (isADefault !== isBDefault) {
        return isADefault ? -1 : 1
      }
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    })
  }, [appointmentTypes, defaultAppointmentType?.id])

  if (!userId || isLoading || calendars.isLoading || messageTemplates.isLoading) {
    return <Loader isLoading={isLoading || calendars.isLoading || messageTemplates.isLoading} />
  }

  const handleCreateAppointmentType = () => {
    setEditingId(undefined)
    setIsModalOpen(true)
  }

  const handleEditAppointmentType = (id: string) => {
    setEditingId(id)
    setIsModalOpen(true)
  }

  const handleDeleteClick = (id: string) => {
    setDeletingId(id)
  }

  const handleConfirmDelete = async () => {
    if (deletingId) {
      try {
        await deleteAppointmentType.mutateAsync(deletingId)
        setDeletingId(undefined)
      } catch (error) {
        console.error('Failed to delete appointment type:', error)
      }
    }
  }

  const handleCancelDelete = () => {
    setDeletingId(undefined)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setModalWasClosed(true)
    setEditingId(undefined)
  }

  const editingAppointmentType = editingId ? appointmentTypes.find((at) => at.id === editingId) : undefined

  return (
    <>
      <div className={classNames(sharedClasses.onboardingSection, classes.bookingAppointmentTypesOnboardingSection)}>
        <div className={classes.headerRow}>
          <div className={classes.backLinkWrapper}>
            <button type="button" onClick={onBack} className={classes.backLink}>
              <Icon name="arrowLeft" className={classes.backLinkIcon} />
              Back to Booking Page
            </button>
            <span className={classes.backLinkSeparator}>|</span>
            <Typography variant="h5" className={sharedClasses.title}>
              Appointment Types
            </Typography>
          </div>
          {appointmentTypes.length > 0 && (
            <Button type="button" onClick={handleCreateAppointmentType} variant="primary" className={classes.addButton}>
              + Add Type
            </Button>
          )}
        </div>
        <Typography variant="body" className={sharedClasses.subtitle}>
          Manage the categories of appointments customers can book
        </Typography>

        {appointmentTypes.length === 0 ? (
          <div className={classes.emptyStateCard}>
            <Typography variant="body" className={classes.emptyStateTitle}>
              No Appointment Types
            </Typography>
            <Typography variant="body" className={classes.emptyStateDescription}>
              Create your first appointment type to start accepting bookings
            </Typography>
            <Button
              type="button"
              onClick={handleCreateAppointmentType}
              variant="primary"
              className={classes.createButton}
            >
              Create your first Appointment Type
            </Button>
          </div>
        ) : (
          <div className={classes.tableCard}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th className={classes.tableHeaderCell}>Name</th>
                  <th className={classes.tableHeaderCell}>Calendar</th>
                  <th className={classes.tableHeaderCell}>Duration</th>
                  <th className={classes.tableHeaderCell}>Buffer Before</th>
                  <th className={classes.tableHeaderCell}>Buffer After</th>
                  <th className={classes.tableHeaderCell}>Minimum Notice</th>
                  <th className={classes.tableHeaderCell}>Auto Reminders</th>
                  <th className={classes.tableHeaderCell}>Rescheduling</th>
                  <th className={classes.tableHeaderCell}>Cancellation Policy</th>
                  <th className={`${classes.tableHeaderCell} ${classes.actionsHeaderCell}`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedAppointmentTypes.map((appointmentType) => (
                  <tr
                    key={appointmentType.id}
                    className={classes.tableRow}
                    onClick={() => handleEditAppointmentType(appointmentType.id)}
                  >
                    <td className={classes.tableCell}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Typography variant="body">{appointmentType.name}</Typography>
                        {appointmentTypes.length > 1 && defaultAppointmentType?.id === appointmentType.id && (
                          <Tooltip
                            title={
                              <span className={classes.defaultTooltip}>
                                Your default appointment type will be automatically selected on your Booking Page.
                              </span>
                            }
                          >
                            <span className={classes.defaultAppointmentType}>Default</span>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                    <td className={classes.tableCell}>
                      <Typography variant="body">{getCalendarName(appointmentType.calendar_id)}</Typography>
                    </td>
                    <td className={classes.tableCell}>
                      <div className={classes.durationCell}>
                        <Icon name="clock" className={classes.clockIcon} />
                        <Typography variant="body">{formatDuration(appointmentType.duration_minutes)}</Typography>
                      </div>
                    </td>
                    <td className={classes.tableCell}>
                      <Typography variant="body" className={classes.notSetText}>
                        {formatBuffer(appointmentType.buffer_before_minutes)}
                      </Typography>
                    </td>
                    <td className={classes.tableCell}>
                      <Typography variant="body" className={classes.notSetText}>
                        {formatBuffer(appointmentType.buffer_after_minutes)}
                      </Typography>
                    </td>
                    <td className={classes.tableCell}>
                      <Typography variant="body" className={classes.notSetText}>
                        {formatMinimumNotice(appointmentType.minimum_notice_minutes)}
                      </Typography>
                    </td>
                    <td className={classes.tableCell}>
                      <Typography variant="body" className={classes.notSetText}>
                        {getMessageTemplateName(appointmentType.set_reminders_using_template_id)}
                      </Typography>
                    </td>
                    <td className={classes.tableCell}>
                      <Typography
                        variant="body"
                        className={
                          bookingOnboarding?.is_rescheduling_enabled && appointmentType.allow_customer_reschedule
                            ? undefined
                            : classes.rescheduleDisabledText
                        }
                      >
                        {bookingOnboarding?.is_rescheduling_enabled && appointmentType.allow_customer_reschedule
                          ? 'Enabled'
                          : 'Disabled'}
                      </Typography>
                    </td>
                    <td className={classes.tableCell}>
                      <TypePolicyBadge appointmentType={appointmentType} />
                    </td>
                    <td className={`${classes.tableCell} ${classes.actionsTableCell}`}>
                      <div className={classes.actionsCell}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleEditAppointmentType(appointmentType.id)
                          }}
                          className={classes.actionButton}
                          title="Edit"
                        >
                          <Icon name="pencil" className={classes.actionIcon} />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteClick(appointmentType.id)
                          }}
                          className={classes.actionButton}
                          title="Delete"
                        >
                          <Icon name="trash" className={classes.actionIcon} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen && (
        <AppointmentTypeModal
          onClose={handleCloseModal}
          appointmentTypeId={editingId}
          initialData={editingAppointmentType}
          appointmentTypesCount={appointmentTypes.length}
          defaultAppointmentTypeId={defaultAppointmentType?.id}
          appointmentTypes={appointmentTypes}
        />
      )}

      {deletingId && (
        <Modal
          onClose={handleCancelDelete}
          title={
            <Typography variant="h3" className={classes.deleteModalTitle}>
              Delete Appointment Type
            </Typography>
          }
          isCentered
          className={classes.deleteModal}
          contentClassName={classes.deleteModalContent}
        >
          <Typography variant="body" className={classes.deleteModalDescription}>
            Are you sure you want to delete this appointment type? This action cannot be undone.
          </Typography>
          {appointmentTypes.length === 1 && (
            <Typography variant="body" className={classes.deleteModalWarning}>
              This is your last active Appointment Type. Deleting it will take your Booking Page offline until a new
              type is added.
            </Typography>
          )}
          <div className={classes.deleteModalButtons}>
            <Button variant="withBorder" onClick={handleCancelDelete} disabled={deleteAppointmentType.isLoading}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmDelete}
              disabled={deleteAppointmentType.isLoading}
              isLoading={deleteAppointmentType.isLoading}
            >
              Delete
            </Button>
          </div>
        </Modal>
      )}
    </>
  )
}
